<script setup>
import { ref, computed } from 'vue';
import { isEmptyObject } from 'dashboard/helper/commons';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useMapGetter } from 'dashboard/composables/store';
import NextButton from 'next/button/Button.vue';
import BaseSettingsHeader from '../../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../../SettingsLayout.vue';
import CreateCustomAppDialog from './CreateCustomAppDialog.vue';
import EditCustomAppDialog from './EditCustomAppDialog.vue';
import DeleteCustomAppDialog from './DeleteCustomAppDialog.vue';
import CustomAppRow from './CustomAppRow.vue';

const { t } = useI18n();

const createCustomAppDialog = ref(null);
const editCustomAppDialog = ref(null);
const deleteCustomAppDialog = ref(null);

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'wgpt_custom_apps'
);
const uiFlags = useMapGetter('integrations/getUIFlags');

const showIntegrationHooks = computed(() => {
  return !uiFlags.value.isFetching && !isEmptyObject(integration.value);
});

const records = computed(() => {
  const hooks = integration.value?.hooks ?? [];
  return hooks.map(({ id, settings }) => {
    const { name, url } = settings ?? {};
    return { id, name, url };
  });
});

const tableHeaders = computed(() => [
  t('WGPT_CUSTOM_APPS.LIST.TABLE.NAME'),
  t('WGPT_CUSTOM_APPS.LIST.TABLE.URL'),
]);

function openAddPopup() {
  createCustomAppDialog.value?.open();
}

function openDeletePopup(response) {
  deleteCustomAppDialog.value?.open(response);
}

function openEditPopup(response) {
  editCustomAppDialog.value?.open(response);
}
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('WGPT_CUSTOM_APPS.LIST.DESCRIPTION')"
    :no-records-message="$t('WGPT_CUSTOM_APPS.LIST.404')"
    :no-records-found="!records.length"
  >
    <template #header>
      <BaseSettingsHeader
        :title="integration.name"
        :description="integration.description"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      >
        <template #actions>
          <NextButton
            blue
            icon="i-lucide-circle-plus"
            :label="$t('WGPT_CUSTOM_APPS.LIST.NEW_CUSTOM_APP')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table
        v-if="showIntegrationHooks"
        class="min-w-full divide-y divide-n-weak"
      >
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right last:pr-4"
          >
            {{ thHeader }}
          </th>
          <th
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right last:pr-4"
          />
        </thead>
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <CustomAppRow
            v-for="(record, index) in records"
            :key="record.id"
            :index="index"
            :custom-app="record"
            @edit="openEditPopup"
            @delete="openDeletePopup"
          />
        </tbody>
      </table>
    </template>

    <CreateCustomAppDialog
      ref="createCustomAppDialog"
      :integration="integration"
    />
    <EditCustomAppDialog ref="editCustomAppDialog" :integration="integration" />
    <DeleteCustomAppDialog
      ref="deleteCustomAppDialog"
      :integration="integration"
    />
  </SettingsLayout>
</template>
